import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel, WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { Amount } from "models/utilTypes/currencyType";
import { UnitValue } from "models/utilTypes/unitValueType";
import { HourEnums, StyleEnums } from "models/enums";
import { CustomLink, User } from "models/propTypes/userProfilePropTypes";
import { StyledDateRange, YearMonth } from "models/propTypes/commonPropTypes";
import { AmountPerUnit } from "models/utilTypes/amountPerUnitType";
import { Hours } from "models/utilTypes/hourType";
import { useFormikContext } from "formik";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { TimeLogsListProps } from "models/propTypes/timeReportingProjectPropTypes";
import { IconDefinitionConstants, RouteConstants, SalaryDeviationConstants } from "utils/constants";
import { Percentage } from "models/utilTypes/percentageType";
//#endregion
//#region components
import { Icon, NestedTable, SimpleTooltip } from "components/common";
import InvoiceStatus from "./_partial/invoiceStatus";
import TimeReportStatus from "./_partial/timeReportStatus";
import CommentsCount from "./_partial/commentsCount";
import { Grid, Tooltip } from "@mui/material";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const TimeLogsList: React.FC<TimeLogsListProps> = (props) => {
  const { value, hiddenColumns, allowCheckBoxSelection, allowPagination, compactTable, onlyBusinessUnits } = props;

  const { values } = useFormikContext<TimeReportingProjectFormTypes.ProjectData>();

  const onSelectionChange = (selectedRows: ApiModel.TimeReportingReportRow[]) => {
    const newRows = [...values.rows];
    props.onChange(newRows);
  };

  const getTimeReportPath = (date: Date, hours?: number, employeeId?: number, parentRowLevel?: number) => {
    const displayText = hours !== undefined ? `${hours} ${HourEnums.HourCode.SHORT}` : "";

    if (parentRowLevel === 0 || hours !== 0) {
      if (onlyBusinessUnits) {
        return displayText;
      }
      const path = `/TimeReport/Weekly?${RouteConstants.TimeReportingQueryParams.EmployeeId}=${employeeId}&${
        RouteConstants.TimeReportingQueryParams.CurrentDate
      }=${LocalizationHelperInstance.getFormattedDate(date)}`;
      return new CustomLink(path, displayText);
    }
    return "";
  };

  const projectTimeWithTimeReportLink = (
    column: ApiModel.TimeReportingReportColumn,
    rowIndex: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: ApiModel.TimeReportingReportRow
  ) => {
    return getTimeReportPath(
      column.fromDate,
      column.projectTime,
      parentRow?.personId,
      parentRow?.timeReportingReportRowLevel
    );
  };

  const internalTimeWithTimeReportLink = (
    column: ApiModel.TimeReportingReportColumn,
    rowIndex: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: any
  ) => {
    return getTimeReportPath(
      column.fromDate,
      column.internalProjectTime,
      parentRow?.personId,
      parentRow?.timeReportingReportRowLevel
    );
  };

  const absenceTimeWithTimeReportLink = (
    column: ApiModel.TimeReportingReportColumn,
    rowIndex: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: ApiModel.TimeReportingReportRow,
    columnName?: string
  ) => {
    return getTimeReportPath(
      column.fromDate,
      column.absence,
      parentRow?.personId,
      parentRow?.timeReportingReportRowLevel
    );
  };

  const formatFromAndToDate = (
    column: ApiModel.TimeReportingReportColumn,
    rowIndex: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: ApiModel.TimeReportingReportRow
  ) => {
    if (column.toDate && column.fromDate !== column.toDate) {
      return new StyledDateRange(column.fromDate, column.toDate);
    }

    if (
      column.deviationType &&
      SalaryDeviationConstants.AllowanceTypes.includes(column.deviationType) &&
      column.deviationType !== WintSuperkollAPIModel.SalaryDeviationType.Allowance &&
      column.deviationType !== WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign &&
      parentRow?.timeReportingReportRowLevel === ApiModel.TimeReportingReportRowLevel.Level3DetailsOrCategorySummary
    ) {
      const yearMonth = DateHelper.getYearMonth(column.fromDate);
      return new YearMonth(yearMonth);
    }

    return LocalizationHelperInstance.getFormattedDate(column.fromDate);
  };

  const formatAllowanceUnit = (
    column: ApiModel.TimeReportingReportColumn,
    rowIndex: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: ApiModel.TimeReportingReportRow
  ) => {
    if (
      parentRow?.timeReportingReportRowLevel !== ApiModel.TimeReportingReportRowLevel.Level3DetailsOrCategorySummary
    ) {
      return UtilsHelper.getCountText(column.allowance);
    }

    switch (column.deviationType) {
      case WintSuperkollAPIModel.SalaryDeviationType.Vacation:
      case WintSuperkollAPIModel.SalaryDeviationType.CareOfChildren:
      case WintSuperkollAPIModel.SalaryDeviationType.Sick:
      case WintSuperkollAPIModel.SalaryDeviationType.ParentalLeave:
      case WintSuperkollAPIModel.SalaryDeviationType.Absence:
      case WintSuperkollAPIModel.SalaryDeviationType.WithdrawalCompensation:
      case WintSuperkollAPIModel.SalaryDeviationType.DepositedCompensation:
      case WintSuperkollAPIModel.SalaryDeviationType.WorkedHours:
      case WintSuperkollAPIModel.SalaryDeviationType.TenDays:
        return new Hours(column.allowance, HourEnums.HourCode.SHORT);
      case WintSuperkollAPIModel.SalaryDeviationType.Allowance:
        return new UnitValue(column.allowance, "d");
      case WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign:
        return new UnitValue(column.allowance, "d");
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionFee:
      case WintSuperkollAPIModel.SalaryDeviationType.Bonus:
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationPaid:
      case WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationSaved:
      case WintSuperkollAPIModel.SalaryDeviationType.WithdrawalsSavedVacation:
        return new Amount(column.currencyCode, column.allowance);
      case WintSuperkollAPIModel.SalaryDeviationType.Mileage:
        return new UnitValue(column.allowance, "km");
      default:
        return column.allowance;
    }
  };

  if (value?.length !== 0) {
    return (
      <NestedTable<ApiModel.TimeReportingReportRow, ApiModel.TimeReportingReportColumn>
        className={compactTable ? cssExports.reportTableContainer : ""}
        rows={value ?? []}
        properties={{
          description: {
            description: TranslationKeys.Common.Name,
            formatter: (r: ApiModel.TimeReportingReportRow) =>
              r.timeReportingReportRowLevel === ApiModel.TimeReportingReportRowLevel.Level1PersonSummary ? (
                new User(r.personId, r.description, false, true)
              ) : (
                <SimpleTooltip text={r.description ?? ""} showOnlyOnOverflow>
                  <Grid item className={clsx(cssExports.truncateString, cssExports.descriptionText)}>
                    {r.description}
                  </Grid>
                </SimpleTooltip>
              ),
            maxWidth: compactTable ? 125 : undefined,
            className: cssExports.freezeCell,
          },
          group: props.allowGrouping
            ? {
                formatter: (r: ApiModel.TimeReportingReportRow) =>
                  LocalizationHelperInstance.getFormattedMessage(
                    r.columns && r.columns[0].timeReportStatus === ApiModel.TimeReportStatus.Attested
                      ? TranslationKeys.Common.Approved
                      : TranslationKeys.Common.NotApproved
                  ),
              }
            : undefined,
        }}
        hiddenColumns={hiddenColumns}
        repeatProperties={{
          key: "columns",
          properties: {
            fromDate: {
              description: TranslationKeys.Common.Date,
              formatter: formatFromAndToDate,
              noWrap: true,
            },
            timeReportStatus: {
              formatter: (r: ApiModel.TimeReportingReportColumn) => (
                <TimeReportStatus status={r.timeReportStatus} approvedBy={r.approvedBy} />
              ),
            },
            commentsCount: {
              formatter: (r: ApiModel.TimeReportingReportColumn) => (
                <CommentsCount commentsCount={r.commentsCount} projectTimeId={r.projectTimeId} />
              ),
            },
            projectTime: {
              description: TranslationKeys.Common.Project,
              formatter: projectTimeWithTimeReportLink,
              alignment: "right",
              noWrap: true,
            },
            internalProjectTime: {
              description: TranslationKeys.TimeReporting.Internal,
              formatter: internalTimeWithTimeReportLink,
              alignment: "right",
              noWrap: true,
            },
            absence: {
              description: TranslationKeys.TimeReporting.AbsenceDeviations,
              formatter: absenceTimeWithTimeReportLink,
              alignment: "right",
              noWrap: true,
            },
            allowance: {
              description: TranslationKeys.TimeReporting.Allowance,
              formatter: formatAllowanceUnit,
              alignment: "right",
              noWrap: true,
            },
            hourlyRate: {
              description: TranslationKeys.TimeReporting.HourlyRate,
              formatter: (r: ApiModel.TimeReportingReportColumn) =>
                r.hourlyRate !== 0 ? new AmountPerUnit(r.currencyCode, r.hourlyRate, HourEnums.HourCode.SHORT) : null,
              alignment: "right",
            },
            calculatedAmount: {
              description: TranslationKeys.TimeReporting.CalculatedAmount,
              formatter: (r: ApiModel.TimeReportingReportColumn) =>
                r.calculatedAmount !== 0 ? new Amount(r.currencyCode, r.calculatedAmount) : null,
              alignment: "right",
              noWrap: true,
            },
            hoursToInvoice: {
              description: TranslationKeys.TimeReporting.HoursToInvoice,
              formatter: (r: ApiModel.TimeReportingReportColumn) =>
                r.hoursToInvoice !== 0 ? new Hours(r.hoursToInvoice, HourEnums.HourCode.SHORT) : null,
              alignment: "right",
              noWrap: true,
            },
            invoicedAmount: {
              description: TranslationKeys.TimeReporting.Invoiced,
              formatter: (r: ApiModel.TimeReportingReportColumn) =>
                r.timeReportInvoiceStatus === ApiModel.TimeReportInvoiceStatus.PartlyCredited ? (
                  <Tooltip
                    placement="top"
                    title={LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.TimeReportingProject.PartiallyCreditedInvoiceAmount
                    )}
                    classes={{ tooltip: cssExports.tooltip, popper: cssExports.tooltipPopper }}
                  >
                    <Grid container item direction="row" justifyContent="flex-end">
                      <Grid item>
                        <Icon
                          icon={IconDefinitionConstants.WExclamation}
                          displayType={StyleEnums.DisplayType.Warning}
                        />
                      </Grid>
                      <Grid item>
                        {LocalizationHelperInstance.getFormattedCurrency(r.invoicedAmount ?? 0, r.currencyCode, 0, 2)}
                      </Grid>
                    </Grid>
                  </Tooltip>
                ) : r.invoicedAmount !== 0 ? (
                  new Amount(r.currencyCode, r.invoicedAmount)
                ) : null,
              alignment: "right",
              noWrap: true,
            },
            timeReportInvoiceStatus: {
              formatter: (values: ApiModel.TimeReportingReportColumn) => (
                <InvoiceStatus
                  status={values.timeReportInvoiceStatus}
                  invoiceId={values.invoiceId}
                  invoiceSerialNumber={values.invoiceSerialNumber}
                  invoices={values.invoices}
                />
              ),
            },
            utilizationRate: {
              description: TranslationKeys.TimeReporting.Utilization,
              formatter: (values: ApiModel.TimeReportingReportColumn) => new Percentage(values.utilizationRate),
              alignment: "right",
              noWrap: true,
            },
          },
        }}
        childrenKey="children"
        hasMultiLevels
        allowCheckBoxSelection={allowCheckBoxSelection}
        onRowSelectionsChange={onSelectionChange}
        pagination={allowPagination && "advanced"}
        maxRowCount={20}
        loading={value === undefined}
      />
    );
  }
  return <div>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NoDataAvailable)}</div>;
};

export default TimeLogsList;
